import React, { useState } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import LoginPage  from './LoginPage';
import Dashboard  from './pages/Dashboard';
import Inventory  from './pages/Inventory';
import Tickets    from './pages/Tickets';
import logo from './logo.png';
import './App.css';

const NAV_ADMIN = [
  { id: 'dashboard', label: 'Στατιστικά', icon: <ChartIcon /> },
  { id: 'tickets',   label: 'Επισκευές',  icon: <TicketIcon /> },
  { id: 'inventory', label: 'Αποθήκη',    icon: <BoxIcon /> },
];
const NAV_TECH = [
  { id: 'tickets',   label: 'Επισκευές',  icon: <TicketIcon /> },
  { id: 'inventory', label: 'Αποθήκη',    icon: <BoxIcon /> },
];
const NAV_CASHIER = [
  { id: 'tickets',   label: 'Επισκευές',  icon: <TicketIcon /> },
];

function AppShell() {
  const { user, logout, isAdmin, isCashier, loading } = useAuth();
  const [page, setPage] = useState(isAdmin ? 'dashboard' : 'tickets');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return window.localStorage.getItem('mg_theme') || 'dark';
  });

  React.useEffect(() => {
    document.body.dataset.theme = theme;
    window.localStorage.setItem('mg_theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: 16 }}>Φόρτωση...</div>
    </div>
  );

  if (!user) return <LoginPage />;

  return (
    <div className="app-layout">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`sidebar${sidebarOpen ? ' sidebar-open' : ''}`}>
        <div className="sidebar-logo">
          <img src={logo} alt="Mr. Gadget" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover' }} />
          <div>
            <div className="logo-title">Mr. Gadget</div>
            <div className="logo-sub">Service Management</div>
          </div>
        </div>

        <nav className="sidebar-nav">
          {(isAdmin ? NAV_ADMIN : isCashier ? NAV_CASHIER : NAV_TECH).map(n => (
            <button key={n.id}
              className={`nav-item ${page === n.id ? 'active' : ''}`}
              onClick={() => { setPage(n.id); setSidebarOpen(false); }}>
              <span className="nav-icon">{n.icon}</span>
              {n.label}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 2 }}>Συνδεδεμένος ως</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{user.username}</div>
            <div style={{
              display: 'inline-block', marginTop: 4, fontSize: 10, fontWeight: 700,
              textTransform: 'uppercase', padding: '2px 8px', borderRadius: 4,
              background: isAdmin ? 'var(--accent)' : isCashier ? '#f59e0b' : 'var(--green)', color: '#fff'
            }}>
              {isAdmin ? 'Admin' : isCashier ? 'Ταμείο' : 'Τεχνικός'}
            </div>
          </div>
          <button onClick={logout} className="btn-ghost"
            style={{ width: '100%', fontSize: 13, textAlign: 'left', padding: '8px 12px' }}>
            Αποσύνδεση
          </button>
          <div className="sidebar-status" style={{ marginTop: 10 }}>
            <span className="status-dot" />Σύστημα Ενεργό
          </div>
        </div>
      </aside>

      {/* Content column */}
      <div className="content-col">
        <div className="content-topbar">
          <button className="hamburger btn-ghost" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <HamburgerIcon />
          </button>
          <button className="theme-toggle btn-ghost" onClick={toggleTheme}>
            {theme === 'dark' ? 'Enter Light Mode' : 'Enter Dark Mode'}
          </button>
        </div>
        <main className="main-content">
          {page === 'dashboard' && isAdmin && <Dashboard />}
          {page === 'tickets'   && <Tickets />}
          {page === 'inventory' && !isCashier && <Inventory />}
        </main>
        <footer className="app-footer">
          <span>© {new Date().getFullYear()} <span>The Mr Gadget</span>. Με επιφύλαξη παντός δικαιώματος.</span>
          <span>Developed by Dimitrios Gkountanos</span>
        </footer>
      </div>
    </div>
  );
}

export default function App() {
  return <AuthProvider><AppShell /></AuthProvider>;
}

function HamburgerIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>;
}
function ChartIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>;
}
function TicketIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2M13 17v2M13 11v2"/></svg>;
}
function BoxIcon() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5M12 22V12"/></svg>;
}